import {
  useFeatureExtractionContext,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import {
  FilterHandlerRecord,
  useProjectDataPanel,
} from '@trimble/project-data-panel-ui';
import {
  SelectionTree,
  SelectionTreeProps,
  SortableKeyOf,
  SortDirection,
  TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import { createMemo, JSX, Show } from 'solid-js';
import ActiveIndicatorChip from '../../components/ActiveIndicatorChip';
import { createMapWorkspaceFolderDescendantRefTrnsGetter } from './fx-workspace-draft-checkbox-sources';
import { fxWorkspaceDraftTrailingContent } from './fx-workspace-draft-checkboxes';
import { ProjectDataFilterMode } from './project-data.types';
import {
  getLayerIdFromNodeId,
  WorkspaceTreeItem,
} from './workspace-tree-nodes';

export interface WorkspacesSelectionTreeContainerProps {
  nodes: TreeNodeDefinition<WorkspaceTreeItem>[];
  childrenLoadStatus?: SelectionTreeProps<WorkspaceTreeItem>['childrenLoadStatus'];
  filterText?: string;
  sortBy: SortableKeyOf<TreeNodeDefinition<WorkspaceTreeItem>>;
  sortDirection: SortDirection;
  filterMode?: ProjectDataFilterMode;
}

export const WorkspacesSelectionTreeContainer = (
  props: WorkspacesSelectionTreeContainerProps
): JSX.Element => {
  const featureExtraction = useFeatureExtractionContext();
  const fxWorkspace = useFxWorkspaceContext();
  const { filterText, filterMode } = useProjectDataPanel();

  const getFolderDescendantRefTrns = createMemo(() =>
    createMapWorkspaceFolderDescendantRefTrnsGetter(
      featureExtraction.editingState.workspaces
    )
  );

  const isLayerNodeVisible = (nodeId: string): boolean => {
    const layerId = getLayerIdFromNodeId(nodeId);
    return layerId !== undefined && featureExtraction.isLayerVisible(layerId);
  };

  const handleVisibilityToggle = (nodeId: string): void => {
    const layerId = getLayerIdFromNodeId(nodeId);
    if (
      layerId === undefined ||
      featureExtraction.layersLoadStatus() !== 'success'
    ) {
      return;
    }
    featureExtraction.toggleLayerVisibility(layerId);
  };

  const isLayerActive = (layerId: string): boolean =>
    layerId === featureExtraction.editingState.selectedLayerId;

  const isNodeSelected = (nodeId: string): boolean => {
    const layerId = getLayerIdFromNodeId(nodeId);
    return layerId !== undefined && isLayerActive(layerId);
  };

  const handleNodeSelect = (
    _nodeId: string,
    node: TreeNodeDefinition<WorkspaceTreeItem>
  ): void => {
    if (node.data?.kind === 'layer' && node.data.layerId !== undefined) {
      featureExtraction.setSelectedLayerId(node.data.layerId);
    }
  };

  const canRenderTree = createMemo(
    () => props.childrenLoadStatus === 'error' || props.nodes.length > 0
  );

  const nodesWithChip = createMemo(
    (): TreeNodeDefinition<WorkspaceTreeItem>[] =>
      props.nodes.map((node) => {
        if (node.data?.kind !== 'layer' || node.data.layerId === undefined) {
          return node;
        }
        const layerId = node.data.layerId;
        return {
          ...node,
          visibleContent: () => (
            <Show when={isLayerActive(layerId)}>
              <ActiveIndicatorChip
                icon="tc-icon-layers"
                tooltip={t(
                  'mobileMapping.cadEditing.projectData.activeLayerTooltip'
                )}
                testId="active-layer-tree-icon"
              />
            </Show>
          ),
        };
      })
  );

  const filterHandlers: FilterHandlerRecord<WorkspaceTreeItem> = {
    all: { filter: () => true },
    active: {
      filter: (node) => isLayerNodeVisible(node.id),
    },
    archived: { filter: () => true },
  };

  return (
    <div data-testid="workspaces-selection-tree">
      <Show when={canRenderTree()} keyed>
        {(_ready) => (
          <SelectionTree
            multiSelect={false}
            childrenLoadStatus={props.childrenLoadStatus ?? 'pending'}
            nodes={nodesWithChip()}
            rowMode={fxWorkspace.isWorkspaceDataEditing() ? 'pick' : 'default'}
            visibleContent={(slotProps) =>
              fxWorkspaceDraftTrailingContent(
                'mapWorkspaces',
                slotProps.node,
                fxWorkspace.isWorkspaceDataEditing(),
                getFolderDescendantRefTrns()
              )
            }
            sortBy={props.sortBy}
            sortDirection={props.sortDirection}
            filterText={props.filterText ?? filterText()}
            filterMode={props.filterMode ?? filterMode()}
            filterHandlers={filterHandlers}
            isNodeLoading={() => false}
            isNodeVisible={isLayerNodeVisible}
            onVisibilityToggle={handleVisibilityToggle}
            isNodeSelected={isNodeSelected}
            onNodeSelect={handleNodeSelect}
          />
        )}
      </Show>
    </div>
  );
};
